<template>
  <section class="ticket-section">
    <div class="rr-header">
      <h2 class="ticket-section-title">Reparaturberichte</h2>
      <button
        v-if="canCreate"
        type="button"
        class="btn btn-primary btn-sm"
        @click="onCreate"
      >Bericht erstellen</button>
    </div>

    <p v-if="loadError" class="error">{{ loadError }}</p>

    <table v-if="reports.length" class="rr-table">
      <thead>
        <tr>
          <th>Bericht</th>
          <th>Datum</th>
          <th>Ergebnis</th>
          <th class="rr-num">Brutto</th>
          <th>Ersteller</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in reports" :key="r.id">
          <td data-label="Bericht">{{ r.id }}</td>
          <td data-label="Datum">{{ formatDate(r.creation_timestamp) }}</td>
          <td data-label="Ergebnis">
            <span class="rr-status" :class="`rr-status-${r.repair_status}`">{{ statusLabel(r.repair_status) }}</span>
            <span v-if="r.is_swap" class="rr-swap" :title="r.swap_imei_sn ?? ''">Austausch</span>
            <span v-if="r.is_goodwill" class="rr-goodwill">Kulanz</span>
          </td>
          <td class="rr-num" data-label="Brutto">{{ r.brutto !== null ? money(r.brutto) : '—' }}</td>
          <td data-label="Ersteller">{{ r.creator_name }}</td>
          <td class="rr-actions">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="pdfBusyId === r.id"
              @click="onOpenPdf(r.id)"
            >{{ pdfBusyId === r.id ? 'Öffne…' : 'PDF' }}</button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary rr-delete-btn"
              :disabled="deleteBusyId === r.id"
              @click="onDelete(r.id)"
            >{{ deleteBusyId === r.id ? 'Lösche…' : 'Löschen' }}</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="!loadError" class="rr-empty">Noch kein Reparaturbericht.</p>

    <RepReportCreateModal ref="createModal" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import RepReportCreateModal from './RepReportCreateModal.vue';
import {
  listRepReports,
  fetchRepReportPdfBlob,
  deleteRepReport,
  type RepReport,
  type RepairStatus,
} from '../services/repReports';
import { hasProcess, PROCESS_REPARATURBERICHT, type TicketDetail } from '../services/tickets';

const props = defineProps<{ ticket: TicketDetail }>();
const emit  = defineEmits<{ changed: [] }>();

const reports      = ref<RepReport[]>([]);
const loadError    = ref<string | null>(null);
const pdfBusyId    = ref<number | null>(null);
const deleteBusyId = ref<number | null>(null);

const createModal = useTemplateRef<InstanceType<typeof RepReportCreateModal>>('createModal');

// Verfügbarkeit kommt vom Server (available_processes) — kein Statusstring im Frontend.
const canCreate = computed(() => hasProcess(props.ticket, PROCESS_REPARATURBERICHT));

async function reload(): Promise<void> {
  try {
    reports.value = await listRepReports(props.ticket.id);
    loadError.value = null;
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Reparaturberichte konnten nicht geladen werden.';
  }
}

onMounted(reload);
watch(() => props.ticket.id, reload);

async function onCreate(): Promise<void> {
  const created = await createModal.value?.open(props.ticket);
  if (created) {
    await reload();
    emit('changed');
  }
}

// openCreate: erlaubt der Prozessleiste, das Anlegen-Formular zu oeffnen.
// reload: nötig, wenn ein Bericht ausserhalb dieses Panels entsteht (kombinierter
// Prozess „KV/Rep-Bericht erstellen“) — die Ticket-ID aendert sich dabei nicht,
// der watch oben feuert also nicht.
defineExpose({ openCreate: onCreate, reload });

async function onDelete(reportId: number): Promise<void> {
  if (deleteBusyId.value !== null) return;
  if (!window.confirm(
    `Reparaturbericht #${reportId} wirklich löschen?\n\n`
    + "Danach lässt sich ein neuer Bericht erstellen. Ein zugehöriger "
    + "Kostenvoranschlag bleibt bestehen und muss bei Bedarf separat gelöscht werden."
  )) return;
  deleteBusyId.value = reportId;
  try {
    await deleteRepReport(props.ticket.id, reportId);
    await reload();
    emit('changed');
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Bericht konnte nicht gelöscht werden.';
  } finally {
    deleteBusyId.value = null;
  }
}

async function onOpenPdf(reportId: number): Promise<void> {
  if (pdfBusyId.value !== null) return;
  pdfBusyId.value = reportId;
  try {
    const blob = await fetchRepReportPdfBlob(props.ticket.id, reportId);
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'PDF konnte nicht geöffnet werden.';
  } finally {
    pdfBusyId.value = null;
  }
}

const STATUS_LABELS: Record<number, string> = { 0: 'Nicht repariert', 1: 'Repariert', 2: 'Totalschaden' };
function statusLabel(s: RepairStatus | null): string {
  return s !== null ? (STATUS_LABELS[s] ?? '—') : '—';
}

function money(value: number): string {
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}
</script>

<style scoped>
.rr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.rr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.rr-table th,
.rr-table td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  text-align: left;
}
.rr-num { text-align: right; font-variant-numeric: tabular-nums; }
.rr-empty { color: var(--muted, #6c757d); margin: 0; }

.rr-status {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
}
.rr-status-1 { background: rgba(40, 167, 69, 0.12); color: #1e7e34; }
.rr-status-0 { background: rgba(108, 117, 125, 0.14); color: #495057; }
.rr-status-2 { background: rgba(215, 0, 21, 0.10); color: var(--danger, #d70015); }
.rr-swap {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  background: #e3f0ff;
  color: #0b5ed7;
}
.rr-goodwill {
  margin-left: 6px;
  font-size: 12px;
  color: var(--muted, #6c757d);
}

.rr-actions {
  display: flex;
  gap: 6px;
  white-space: nowrap;
}
.rr-delete-btn:hover:not(:disabled) {
  color: var(--danger, #d70015);
  border-color: rgba(215, 0, 21, 0.35);
  background: rgba(215, 0, 21, 0.06);
}

/* Handyansicht: Tabelle → gestapelte Karten (Label : Wert). */
@media (max-width: 640px) {
  .rr-header { flex-wrap: wrap; gap: 8px; }
  .rr-table thead { display: none; }
  .rr-table,
  .rr-table tbody,
  .rr-table tr,
  .rr-table td { display: block; width: 100%; }
  .rr-table tr {
    border: 1px solid var(--border, #e5e7eb);
    border-radius: var(--radius-sm, 8px);
    padding: 8px 12px;
    margin-bottom: 10px;
  }
  .rr-table td {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 3px 0;
    border: none;
    text-align: right;
  }
  .rr-table td::before {
    content: attr(data-label);
    color: var(--muted, #6c757d);
    font-weight: 600;
    text-align: left;
  }
  .rr-num { text-align: right; }
  .rr-actions { justify-content: flex-end; padding-top: 8px; }
  .rr-actions::before { display: none; }
  .rr-actions .btn { flex: 1 1 auto; }
}
</style>
